import { zodResolver } from "@hookform/resolvers/zod";
import {
  SHORTAGE_POLICIES,
  SHORTAGE_POLICY_LABELS,
  type SaveShiftSettings,
  type ShiftSettings,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  MoneyInput,
  Select,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, type ReactNode, useEffect, useId, useState } from "react";
import { Controller, type FieldErrors, type Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCompanyStations } from "../../../features/accounting/shared.js";
import { useRefreshShifts, useShiftPermissions, useShiftSettings } from "../../../features/shifts/queries.js";
import {
  type ShiftSettingsFormValues,
  shiftSettingsFormFrom,
  shiftSettingsFormSchema,
} from "../../../features/shifts/settings-values.js";
import { ShiftsPage, shiftsSearchShape } from "../../../features/shifts/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";

const searchSchema = z.object({
  ...shiftsSearchShape,
  /** "company" ya da bir istasyon kimliği: ayar satırı ŞİRKET ya da İSTASYON geçersiz kılmasıdır. */
  scope: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/vardiya/ayarlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ShiftSettingsPage,
});

/**
 * Vardiya ayarları (akaryakıt ayar sayfasıyla aynı desen): şirket varsayılanı + istasyon geçersiz kılması.
 * Hiçbir eşik koda gömülmez (AGENTS.md §3.12); seed değerleri "doğrulanmalı" olarak işaretlidir.
 * PUT kapsamın satırını TAMAMEN değiştirir ve iyimser kilit (`version`) uygular.
 */
function ShiftSettingsPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  return (
    <ShiftsPage
      title={t("menu.shiftSettings")}
      description={t("shifts.settings.description")}
      breadcrumb={[{ label: t("menu.shiftSettings") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <SettingsBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        )
      }
    </ShiftsPage>
  );
}

function SettingsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const stations = useCompanyStations(companyId);
  const scope = search.scope ?? "company";
  const scopeStationId = scope === "company" ? null : scope;
  const settings = useShiftSettings(companyId, scopeStationId);

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField
          label={t("shifts.settings.scope")}
          description={t("shifts.settings.scopeHint")}
          className="w-72"
        >
          {(control) => (
            <Select
              {...control}
              value={scope}
              onChange={(event) =>
                void navigate({
                  search: (previous) => ({ ...previous, scope: event.target.value }),
                  replace: true,
                })
              }
            >
              <option value="company">{t("shifts.settings.companyScope")}</option>
              {(stations.data ?? []).map((station) => (
                <option key={station.id} value={station.id}>
                  {station.code} {station.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </Card>

      {settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : !settings.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <SettingsForm
          key={`${companyId}:${scope}`}
          settings={settings.data as ShiftSettings}
          scopeStationId={scopeStationId}
          onReload={() => void settings.refetch()}
        />
      )}
    </>
  );
}

function errorAt(errors: FieldErrors<ShiftSettingsFormValues>, name: string): string | undefined {
  const message = (errors as Record<string, { message?: unknown } | undefined>)[name]?.message;
  return typeof message === "string" ? message : undefined;
}

function SettingsForm({
  settings,
  scopeStationId,
  onReload,
}: {
  settings: ShiftSettings;
  scopeStationId: string | null;
  onReload: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshShifts();
  const canManage = useShiftPermissions().canManageSettings;
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // İstasyon kapsamında henüz geçersiz kılma yoksa şirket satırının değerleri gösterilir; hedef sürüm 0'dır.
  const overrideExists = scopeStationId !== null && settings.stationId === scopeStationId;
  const isDefaultRow = scopeStationId === null ? settings.isDefault : !overrideExists;
  const targetVersion = scopeStationId === null ? settings.version : overrideExists ? settings.version : 0;

  const form = useForm<ShiftSettingsFormValues, unknown, SaveShiftSettings>({
    resolver: zodResolver(shiftSettingsFormSchema),
    defaultValues: shiftSettingsFormFrom(settings, {
      stationId: scopeStationId,
      version: targetVersion,
    }),
  });
  const {
    control,
    formState: { errors },
    register,
    setValue,
    watch,
  } = form;

  useEffect(() => {
    form.reset(shiftSettingsFormFrom(settings, { stationId: scopeStationId, version: targetVersion }));
    // Sunucudan yeni ayar geldiğinde (yenile / kaydet sonrası) form tazelenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, scopeStationId, targetVersion]);

  const save = useMutation({
    mutationFn: async (body: SaveShiftSettings) =>
      (await api.PUT("/shifts/settings", { body: body as never })).data!,
    onSuccess: async (saved) => {
      await refresh();
      form.reset(
        shiftSettingsFormFrom(saved as ShiftSettings, {
          stationId: scopeStationId,
          version: (saved as ShiftSettings).version,
        }),
      );
      toast({ tone: "success", title: t("shifts.settings.saved") });
    },
    onError: (caught) => {
      if (isApiError(caught, "VERSION_CONFLICT")) {
        setConflict(true);
        return;
      }
      setError(errorMessage(caught));
    },
  });

  const submit = form.handleSubmit((values) => {
    setError(null);
    setConflict(false);
    save.mutate(values);
  });

  const needsVerification = watch("needsVerification");

  const moneyField = (
    name: "differenceToleranceAmount" | "defaultOpeningFloat" | "zMismatchThreshold",
    label: string,
    description: string,
  ): ReactElement => (
    <FormField label={label} description={description} error={errorAt(errors, name)}>
      {(fieldControl) => (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <MoneyInput
              {...fieldControl}
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              disabled={!canManage}
            />
          )}
        />
      )}
    </FormField>
  );

  const textField = (
    name: Path<ShiftSettingsFormValues>,
    label: string,
    description: string,
    options: { integer?: boolean } = {},
  ): ReactElement => (
    <FormField label={label} description={description} error={errorAt(errors, name)}>
      {(fieldControl) => (
        <Input
          {...fieldControl}
          {...register(name)}
          {...(options.integer ? { type: "number", inputMode: "numeric" } : { inputMode: "decimal" })}
          disabled={!canManage}
        />
      )}
    </FormField>
  );

  const toggle = (name: Path<ShiftSettingsFormValues>, label: string, description: string): ReactElement => (
    <label className="flex min-h-11 items-start gap-2 text-sm sm:col-span-2">
      <Checkbox className="mt-1 size-5" {...register(name)} disabled={!canManage} />
      <span className="grid gap-0.5">
        <span className="font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </label>
  );

  return (
    <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
      {conflict ? (
        <Alert tone="warning" title={t("shifts.settings.conflictTitle")}>
          {t("shifts.settings.conflictBody")}{" "}
          <Button variant="outline" size="sm" onClick={onReload}>
            {t("shifts.settings.reload")}
          </Button>
        </Alert>
      ) : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {canManage ? null : <Alert tone="info">{t("shifts.settings.readOnly")}</Alert>}
      {isDefaultRow ? (
        <Alert tone="info" title={t("shifts.settings.isDefaultTitle")}>
          {scopeStationId === null ? t("shifts.settings.isDefaultBody") : t("shifts.settings.noOverrideBody")}
        </Alert>
      ) : null}
      {needsVerification ? (
        <Alert tone="warning" title={t("shifts.settings.needsVerificationTitle")}>
          <div className="grid gap-2">
            <p>{t("shifts.settings.needsVerificationBody")}</p>
            {canManage ? (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setValue("needsVerification", false, { shouldDirty: true });
                    void submit();
                  }}
                >
                  {t("shifts.settings.markVerified")}
                </Button>
              </div>
            ) : null}
          </div>
        </Alert>
      ) : (
        <p>
          <StatusBadge tone="success">{t("shifts.settings.verified")}</StatusBadge>
        </p>
      )}

      <FormSection title={t("shifts.settings.sections.difference")}>
        {moneyField(
          "differenceToleranceAmount",
          t("shifts.settings.differenceToleranceAmount"),
          t("shifts.settings.differenceToleranceAmountHint"),
        )}
        {textField(
          "differenceTolerancePerMille",
          t("shifts.settings.differenceTolerancePerMille"),
          t("shifts.settings.differenceTolerancePerMilleHint"),
        )}
        <FormField
          label={t("shifts.settings.shortagePolicy")}
          description={t("shifts.settings.shortagePolicyHint")}
          error={errorAt(errors, "shortagePolicy")}
        >
          {(fieldControl) => (
            <Select {...fieldControl} {...register("shortagePolicy")} disabled={!canManage}>
              {SHORTAGE_POLICIES.map((policy) => (
                <option key={policy} value={policy}>
                  {SHORTAGE_POLICY_LABELS[policy]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        {toggle(
          "chargeWithinTolerance",
          t("shifts.settings.chargeWithinTolerance"),
          t("shifts.settings.chargeWithinToleranceHint"),
        )}
      </FormSection>

      <FormSection title={t("shifts.settings.sections.opening")}>
        {toggle(
          "allowOpenWithPreviousUnclosed",
          t("shifts.settings.allowOpenWithPreviousUnclosed"),
          t("shifts.settings.allowOpenWithPreviousUnclosedHint"),
        )}
        {textField(
          "openingTotalizerToleranceL",
          t("shifts.settings.openingTotalizerToleranceL"),
          t("shifts.settings.openingTotalizerToleranceLHint"),
        )}
        {textField(
          "transactionMismatchL",
          t("shifts.settings.transactionMismatchL"),
          t("shifts.settings.transactionMismatchLHint"),
        )}
        {moneyField(
          "defaultOpeningFloat",
          t("shifts.settings.defaultOpeningFloat"),
          t("shifts.settings.defaultOpeningFloatHint"),
        )}
        {textField(
          "openShiftAlertHours",
          t("shifts.settings.openShiftAlertHours"),
          t("shifts.settings.openShiftAlertHoursHint"),
          { integer: true },
        )}
      </FormSection>

      <FormSection title={t("shifts.settings.sections.dayEnd")}>
        {toggle(
          "requireTankClosingReadings",
          t("shifts.settings.requireTankClosingReadings"),
          t("shifts.settings.requireTankClosingReadingsHint"),
        )}
        {textField(
          "tankClosingReadingWindowMinutes",
          t("shifts.settings.tankClosingReadingWindowMinutes"),
          t("shifts.settings.tankClosingReadingWindowMinutesHint"),
          { integer: true },
        )}
        {toggle(
          "requireZReports",
          t("shifts.settings.requireZReports"),
          t("shifts.settings.requireZReportsHint"),
        )}
        {moneyField(
          "zMismatchThreshold",
          t("shifts.settings.zMismatchThreshold"),
          t("shifts.settings.zMismatchThresholdHint"),
        )}
        {toggle(
          "blockOnZMismatch",
          t("shifts.settings.blockOnZMismatch"),
          t("shifts.settings.blockOnZMismatchHint"),
        )}
        {toggle(
          "zCompareExcludesCreditSales",
          t("shifts.settings.zCompareExcludesCreditSales"),
          t("shifts.settings.zCompareExcludesCreditSalesHint"),
        )}
        {toggle(
          "enforceDayOrder",
          t("shifts.settings.enforceDayOrder"),
          t("shifts.settings.enforceDayOrderHint"),
        )}
      </FormSection>

      {canManage ? (
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function FormSection({ title, children }: { title: string; children: ReactNode }): ReactElement {
  const headingId = useId();
  return (
    <Card className="grid gap-4 p-4 sm:p-5" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-base font-semibold">
        {title}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
    </Card>
  );
}
